import { signal, computed, type Signal, type ReadonlySignal } from '@preact/signals';
import type { Archetype } from '@/lib/archetypes';
import type { DailyRecord, StreakState } from '@/lib/local-state';
import type { Outcome } from '@/lib/shot-resolution';
import type { Zone } from '@/lib/zones';
import type { CalloutMode, PlayState } from '@/bridge/gameBus';
import type { GameMode } from '@/lib/game-mode';

export type Locale = 'hu' | 'sk';
export type SceneKey = 'BOOT' | 'MENU' | 'PLAY' | 'RESULT';

function initialLocale(): Locale {
  if (typeof window === 'undefined') return 'hu';
  try {
    const u = new URL(window.location.href);
    const q = u.searchParams.get('locale');
    if (q === 'sk') return 'sk';
  } catch { /* ignore */ }
  return 'hu';
}

const currentScene: Signal<SceneKey | null> = signal(null);
const dateKey: Signal<string | null> = signal(null);
const puzzleNumber: Signal<number | null> = signal(null);
const shotIndex: Signal<number> = signal(0);
const playState: Signal<PlayState | null> = signal(null);
const lastShotOutcome: Signal<Outcome | null> = signal(null);
const record: Signal<DailyRecord | null> = signal(null);
const streak: Signal<StreakState | null> = signal(null);
const archetype: Signal<Archetype | null> = signal(null);
const observations: Signal<Record<Zone, number>> = signal({ LF: 0, CF: 0, RF: 0, LA: 0, CA: 0, RA: 0 });
const pulseZone: Signal<Zone | null> = signal(null);
const cardMode: Signal<CalloutMode> = signal('preplay');
const currentLocale: Signal<Locale> = signal(initialLocale());
const todayAverage: Signal<number | null> = signal(null);
const todayPlayerCount: Signal<number | null> = signal(null);
const hideHeatmap: Signal<boolean> = signal(false);
const assistAvailable: Signal<boolean> = signal(false);
const assistHint: Signal<Zone | null> = signal(null);
const gameMode: Signal<GameMode> = signal('daily');
const practiceRecord: Signal<DailyRecord | null> = signal(null);

const completedShotCount: ReadonlySignal<number> = computed(
  () => record.value?.shots.length ?? 0,
);

export const gameSignals = {
  currentScene,
  dateKey,
  puzzleNumber,
  shotIndex,
  playState,
  lastShotOutcome,
  record,
  streak,
  archetype,
  observations,
  pulseZone,
  cardMode,
  currentLocale,
  todayAverage,
  todayPlayerCount,
  hideHeatmap,
  assistAvailable,
  assistHint,
  gameMode,
  practiceRecord,
  completedShotCount,
};
